import * as THREE from "three";
import { EventBus } from "../EventBus.js";
import { WorldStore } from "../../store/WorldStore.js";
import { INTERACTIVE_LAYER } from "../renderer/Raycaster.js";
import type { LightMetadata } from "../types/tiled.js";
import type { Position } from "../types/grid.js";

/**
 * Authored ceiling lights (W22).
 *
 * Owns the lifecycle of one `THREE.PointLight` per authored `light` Tiled
 * object plus an invisible raycast target so the light itself is
 * clickable (lets a map ship the toggle feature without authoring a
 * wall-switch prop). `world:light_toggled` from `WorldStore.toggleLight`
 * flips each light's `visible` flag in place — same pattern as
 * `DoorRenderer`.
 *
 * ### Uniform budget
 *
 * Three.js compiles a shader per distinct `NUM_POINT_LIGHTS` count it
 * sees. The game already ships one permanent point light; maps with
 * more than `SOFT_CAP` authored lights force a recompile every time
 * the player crosses a doorway, so we soft-cap at 8 and drop the
 * farthest-from-player lights with a console warning. Revisit with a
 * nearest-N-on-movement scheme if a real map needs to break the cap.
 */

const SOFT_CAP = 8;

export interface LightDescriptor {
  readonly cell: Position;
  readonly meta: LightMetadata;
}

interface LightEntry {
  readonly meta: LightMetadata;
  readonly light: THREE.PointLight;
  readonly pickTarget: THREE.Mesh;
}

export class LightManager {
  private readonly entries = new Map<string, LightEntry>();
  private readonly unsub: () => void;
  private readonly pickGeometry = new THREE.SphereGeometry(0.2, 6, 6);
  private readonly pickMaterial = new THREE.MeshBasicMaterial({ visible: false });

  constructor(
    private readonly scene: THREE.Scene,
    descriptors: ReadonlyArray<LightDescriptor>,
    getPlayerCell: () => Position,
  ) {
    const kept = this.applySoftCap(descriptors, getPlayerCell());

    for (const descriptor of kept) {
      const { meta, cell } = descriptor;

      // Seed the WorldStore with author-intent if this light has not
      // been toggled yet this session. `setLightInitialState` only adds
      // to `lightsOff` — a saved "on" flip survives a reload.
      WorldStore.setLightInitialState(meta.light_id, meta.initial_state === "off");

      const color = new THREE.Color(meta.color);
      const light = new THREE.PointLight(color, meta.intensity, meta.range);
      light.position.set(cell.x, meta.height, cell.z);
      light.visible = !WorldStore.isLightOff(meta.light_id);
      this.scene.add(light);

      const pickTarget = new THREE.Mesh(this.pickGeometry, this.pickMaterial);
      pickTarget.position.copy(light.position);
      pickTarget.userData = {
        instanceId: meta.light_id,
        kind: "light",
        cellX: cell.x,
        cellZ: cell.z,
      };
      pickTarget.layers.set(INTERACTIVE_LAYER);
      this.scene.add(pickTarget);

      this.entries.set(meta.light_id, { meta, light, pickTarget });
    }

    this.unsub = EventBus.on("world:light_toggled", ({ lightId, off }) => {
      const entry = this.entries.get(lightId);
      if (!entry) return;
      entry.light.visible = !off;
    });
  }

  /** True if a light with that id is registered on the current map. */
  has(lightId: string): boolean {
    return this.entries.has(lightId);
  }

  /** All authored light ids on the current map (for Ink observed-variable registration). */
  lightIds(): readonly string[] {
    return [...this.entries.keys()];
  }

  dispose(): void {
    this.unsub();
    for (const { light, pickTarget } of this.entries.values()) {
      this.scene.remove(light);
      this.scene.remove(pickTarget);
      light.dispose();
    }
    this.entries.clear();
    this.pickGeometry.dispose();
    this.pickMaterial.dispose();
  }

  /**
   * Drop farthest-from-player lights until we're within the soft cap.
   * Emits a `console.warn` so the author notices during map review.
   */
  private applySoftCap(
    descriptors: ReadonlyArray<LightDescriptor>,
    playerCell: Position,
  ): ReadonlyArray<LightDescriptor> {
    if (descriptors.length <= SOFT_CAP) return descriptors;
    console.warn(
      `[LightManager] ${String(descriptors.length)} authored lights exceeds soft cap of ${String(
        SOFT_CAP,
      )} — dropping farthest-from-player. Consider splitting the map or adding a nearest-N scheme.`,
    );
    const ranked = [...descriptors].sort((a, b) => {
      const da = sqDistance(a.cell, playerCell);
      const db = sqDistance(b.cell, playerCell);
      return da - db;
    });
    return ranked.slice(0, SOFT_CAP);
  }
}

function sqDistance(a: Position, b: Position): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}
